import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchJobById } from '../../api/jobs';
import { Job } from '../../types/api';
import api from '../../api/http';
import { getFullPhotoUrl } from '../../utils/imageHelper';
import '../../App.css';

interface ServiceRecordData {
  id: number;
  service_type: string;
  service_date: string;
  status: string;
  notes?: string;
  photos: {
    id: number;
    photo_type: string;
    photo_url: string;
    photo_order: number;
  }[];
  user?: {
    id: number;
    name: string;
  };
}

interface ProductRecordData {
  product: {
    id: number;
    name: string;
    model?: string;
    item_category?: {
      id: number;
      name: string;
    };
  };
  quantity_found: number; // 產品數量（從 session）
  product_status: string; // 產品狀態（從 session）
  service_date: string; // 服務日期（從 session）
  services: ServiceRecordData[];
}

interface DamagePhotoData {
  id: number;
  photo_url: string;
  photo_order: number;
  notes?: string;
  damage_severity?: string;
  uploaded_at: string;
}

interface SubmittedRecordsData {
  records_by_product: { [productId: string]: ProductRecordData };
  damage_photos_by_product: { [productId: string]: DamagePhotoData[] };
  total_products: number;
  total_records: number;
}

const ViewProductRecordPage: React.FC = () => {
  const navigate = useNavigate();
  const { jobId, storeId, productId } = useParams<{ jobId: string; storeId: string; productId: string }>();

  // 獲取 Job 詳情
  const { data: job, isLoading: isLoadingJob } = useQuery<Job>({
    queryKey: ['job', jobId],
    queryFn: () => fetchJobById(jobId as string),
    enabled: Boolean(jobId),
  });

  // 獲取已提交的服務記錄
  const { data: submittedRecords, isLoading: isLoadingRecords } = useQuery<SubmittedRecordsData>({
    queryKey: ['service-records', 'by-job-store', jobId, storeId],
    queryFn: async () => {
      const { data } = await api.get('/service-records/by-job-store', {
        params: { job_id: jobId, store_id: storeId },
      });
      return data;
    },
    enabled: Boolean(jobId) && Boolean(storeId),
  });

  const productData = productId && submittedRecords ? submittedRecords.records_by_product?.[productId] : null;
  const damagePhotos = productId && submittedRecords ? submittedRecords.damage_photos_by_product?.[productId] || [] : [];
  const product = productData?.product;

  const currentStore = React.useMemo(() => {
    return job?.stores?.find(s => s.id === Number(storeId));
  }, [job, storeId]);

  if (isLoadingJob || isLoadingRecords) {
    return <div className="panel">載入中...</div>;
  }

  if (!job || !productData || !product) {
    return (
      <div className="panel">
        <p>無法載入產品服務記錄</p>
        <button onClick={() => navigate(`/jobs/${jobId}/store/${storeId}/products`)}>返回店鋪產品列表</button>
      </div>
    );
  }

  const hasDamage = damagePhotos.length > 0;

  // 服務類型中文映射
  const getServiceTypeName = (serviceType: string): string => {
    const typeMap: { [key: string]: string } = {
      'inventory': '盤點',
      'cleaning': '清潔',
      'pop_replacement': 'POP 更換',
    };
    return typeMap[serviceType] || serviceType;
  };

  return (
    <div className="panel" style={{ maxWidth: '100%', overflowX: 'hidden' }}>
      {/* 頁首 */}
      <div style={{ marginBottom: '2rem' }}>
        <button
          onClick={() => navigate(`/jobs/${jobId}/store/${storeId}/products`)}
          className="link-back"
          style={{ display: 'inline-block', marginBottom: '1rem', fontSize: '1.125rem', fontWeight: '600' }}
        >
          ← 返回產品列表
        </button>

        <h2 style={{ fontSize: '1.75rem', fontWeight: 700, margin: '0 0 1rem 0' }}>查看服務記錄</h2>
        
        <div style={{ padding: '1.5rem', backgroundColor: '#f3f4f6', borderRadius: '8px', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.75rem' }}>{job.job_name}</h3>
          
          {/* 店鋪信息 */}
          <div style={{ marginBottom: '0.5rem' }}>
            <p style={{ fontSize: '1.125rem', fontWeight: 700, margin: '0 0 0.5rem 0', color: '#1f2937' }}>
              {currentStore?.retailer?.name}
            </p>
          </div>
          
          {/* 詳細地址 */}
          <div style={{ fontSize: '1rem', color: '#6b7280', lineHeight: '1.5' }}>
            {/* 區域 */}
            {(currentStore?.building?.district?.region?.name || currentStore?.building?.district?.name) && (
              <p style={{ margin: '0.25rem 0', fontWeight: 500 }}>
                {currentStore.building?.district?.region?.name || ''} {currentStore.building?.district?.name || ''}
              </p>
            )}
            
            {/* 建築物 */}
            {currentStore?.building?.building_name && (
              <p style={{ margin: '0.25rem 0', fontWeight: 500 }}>
                {currentStore.building.building_name}
              </p>
            )}
            
            {/* 樓層和單位 */}
            {(currentStore?.floor || currentStore?.unit) && (
              <p style={{ margin: '0.25rem 0', fontWeight: 500 }}>
                {currentStore.floor && currentStore.floor}
                {currentStore.floor && currentStore.unit && ' '}
                {currentStore.unit && currentStore.unit}
              </p>
            )}
          </div>
        </div>

        {/* 產品信息 */}
        <div style={{ 
          padding: '1.5rem', 
          border: '2px solid',
          borderColor: hasDamage ? '#fca5a5' : '#e5e7eb',
          borderRadius: '8px',
          backgroundColor: hasDamage ? '#fef2f2' : 'white',
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
        }}>
          <div style={{ marginBottom: '0.5rem' }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 700, margin: '0 0 0.5rem 0', wordWrap: 'break-word' }}>
              {product.name}
            </h3>
            {hasDamage && (
              <span style={{ 
                display: 'inline-block',
                padding: '0.25rem 0.75rem',
                borderRadius: '6px',
                backgroundColor: '#fee2e2',
                fontSize: '1.125rem',
                color: '#dc2626',
                fontWeight: 700
              }}>
                ⚠️ 損壞
              </span>
            )}
          </div>
          {product.model && (
            <p style={{ fontSize: '1.125rem', color: '#6b7280', marginBottom: '0.5rem', fontWeight: 500 }}>
              型號：{product.model}
            </p>
          )}
          {product.item_category && (
            <span style={{ 
              display: 'inline-block',
              padding: '0.25rem 0.75rem', 
              backgroundColor: '#e0e7ff', 
              color: '#4f46e5', 
              borderRadius: '6px',
              fontSize: '1rem',
              fontWeight: 600
            }}>
              {product.item_category.name}
            </span>
          )}
          
          {/* 產品數量和狀態 - 從 session 獲取 */}
          <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
            <p style={{ fontSize: '1.125rem', fontWeight: 700, margin: '0.25rem 0', color: '#1f2937' }}>
              數量：{productData.quantity_found}
            </p>
            <p style={{ fontSize: '1.125rem', fontWeight: 700, margin: '0.25rem 0', color: productData.product_status === 'damaged' ? '#dc2626' : '#10b981' }}>
              狀態：
              {productData.product_status === 'normal' ? '✅ 正常' : 
               productData.product_status === 'damaged' ? '⚠️ 損壞' : 
               productData.product_status === 'not_found' ? '❌ 不存在' : productData.product_status}
            </p>
          </div>
        </div>
      </div>

      {/* 損壞照片 */}
      {hasDamage && (
        <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fee2e2', borderRadius: '8px', border: '2px solid #fca5a5' }}>
          <h4 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#dc2626', marginBottom: '1rem' }}>
            產品損壞照片 ({damagePhotos.length} 張)
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '1rem' }}>
            {damagePhotos.map((photo) => (
              <div key={photo.id}>
                <img
                  src={getFullPhotoUrl(photo.photo_url)}
                  alt={`損壞照片 ${photo.photo_order + 1}`}
                  style={{ 
                    width: '100%', 
                    height: '150px', 
                    objectFit: 'cover', 
                    borderRadius: '8px', 
                    border: '2px solid #fca5a5',
                    cursor: 'pointer'
                  }}
                  onClick={() => window.open(getFullPhotoUrl(photo.photo_url), '_blank')}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 服務記錄 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <h3 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>
          服務記錄 ({productData.services.length} 項)
        </h3>
        
        {productData.services.map((service) => (
          <div
            key={service.id}
            style={{
              padding: '1.5rem',
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '2px solid #e5e7eb',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            <div style={{ marginBottom: '1rem' }}>
              <h4 style={{ fontSize: '1.5rem', fontWeight: 700, margin: '0 0 0.75rem 0' }}>
                {getServiceTypeName(service.service_type)}
              </h4>
              <span style={{ 
                display: 'inline-block',
                padding: '0.5rem 1rem',
                backgroundColor: service.status === 'completed' ? '#10b981' : '#9ca3af',
                color: 'white',
                borderRadius: '6px',
                fontSize: '1.125rem',
                fontWeight: 700
              }}>
                ✓ {service.status === 'completed' ? 'completed' : service.status}
              </span>
            </div>

            {service.user && (
              <p style={{ fontSize: '1.125rem', color: '#6b7280', marginBottom: '0.5rem', fontWeight: 600 }}>
                服務員工：{service.user.name}
              </p>
            )}

            <p style={{ fontSize: '1.125rem', color: '#6b7280', marginBottom: '1rem', fontWeight: 500 }}>
              服務日期：{service.service_date.split('T')[0]}
            </p>

            {/* 備註 */}
            {service.notes && (
              <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#fef3c7', borderRadius: '8px', border: '1px solid #fbbf24' }}>
                <p style={{ fontSize: '1.125rem', fontWeight: 600, color: '#92400e', margin: 0 }}>
                  備註：{service.notes}
                </p>
              </div>
            )}

            {/* 服務照片 */}
            {service.photos && service.photos.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <h5 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.75rem' }}>
                  服務照片 ({service.photos.length} 張)
                </h5>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '0.75rem' }}>
                  {service.photos.map((photo) => (
                      <div key={photo.id} style={{ position: 'relative' }}>
                        <img
                          src={getFullPhotoUrl(photo.photo_url)}
                          alt={`${photo.photo_type} ${photo.photo_order + 1}`}
                          style={{ 
                            width: '100%', 
                            height: '120px', 
                            objectFit: 'cover', 
                            borderRadius: '8px', 
                            border: '1px solid #e5e7eb',
                            cursor: 'pointer'
                          }}
                          onClick={() => window.open(getFullPhotoUrl(photo.photo_url), '_blank')}
                        />
                        <span style={{ 
                          position: 'absolute',
                          bottom: '4px',
                          left: '4px',
                          padding: '0.25rem 0.5rem',
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          color: 'white',
                          fontSize: '0.875rem',
                          fontWeight: 700,
                          borderRadius: '4px'
                        }}>
                          {photo.photo_type === 'before' ? '前' : photo.photo_type === 'after' ? '後' : '一般'}
                        </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ViewProductRecordPage;

